import { FC, ReactElement, useEffect, useState } from 'react';
import AppModal from '../app-modal/AppModal';
import { Button, Modal } from 'react-bootstrap';
import i18next from 'i18next';
import DropZone from '../DropZone';
import { useAuthFetchFromRestAPI } from '../../util/api';
import { useS3Upload } from '../../hooks/useS3Upload/useS3Upload';
import { DocumentUploadDto } from '../../../../shared/dtos/upload.dto';
import { DocumentUploadRequest } from '../../../../shared/requests/documentUploadRequest';
import { PatientDocumentRequest } from '../../../../shared/requests/patientDocument.request';
import UploadOverlay, { Upload } from '../UploadOverlay';

type PatientFileUploadModalProps = {
  show: boolean;
  patientId: string;
  isCompletion: boolean;
  onClose: () => void;
  onSave: () => void;
}

const PatientFileUploadModal: FC<PatientFileUploadModalProps> = ({
  show,
  patientId,
  isCompletion,
  onClose,
  onSave
}: PatientFileUploadModalProps): ReactElement => {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState<Upload>({ progress: null, status: 'idle' });
  const [isSaving, setIsSaving] = useState(false);

  const { authFetch } = useAuthFetchFromRestAPI();
  const { uploadFile } = useS3Upload();

  useEffect(() => {
    return (): void  => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const resetState = (): void => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setFile(null);
    setPreviewUrl(null);
    setUploadState({ progress: null, status: 'idle' });
    setIsSaving(false);
  };

  const handleHide = (): void => {
    resetState();
    onClose();
  };

  const handleFileSelect = (file: File): void => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setUploadState({ progress: null, status: 'idle' });
  };

  const handleClear = (): void => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setFile(null);
    setPreviewUrl(null);
    setUploadState({ progress: null, status: 'idle' });
  };

  const createPresignedUrl = async (selectedFile: File): Promise<DocumentUploadDto> => {
    const request: DocumentUploadRequest = {
      patientId: patientId,
      isCompletion: isCompletion,
      documentContentType: selectedFile.type
    };

    return authFetch('/api/v1/file-upload/document', { method: 'POST', body: request });
  };

  const updatePatient = async (fileUrl: string): Promise<void> => {
    const request: PatientDocumentRequest = {
      fileUrl: fileUrl,
      isCompletion: isCompletion
    };

    return authFetch(`/api/v1/patients/document/${patientId}`, { method: 'PATCH', body: request });
  };

  const handleSave = async (): Promise<void> => {
    if (!file || isSaving) {
      return;
    }

    try {
      setIsSaving(true);
      setUploadState({ progress: 0, status: 'uploading' });

      const presignedUrl: DocumentUploadDto = await createPresignedUrl(file);

      await uploadFile(
        presignedUrl.document.uploadUrl,
        file,
        (progress: number) => {
          setUploadState(previous => ({ ...previous, progress }));
        },
      );

      setUploadState({ progress: null, status: 'success' });

      await updatePatient(presignedUrl.document.fileUrl);

      resetState();
      onSave();
    } catch (error) {
      console.error(error);
      setUploadState({ progress: null, status: 'error' });
      setIsSaving(false);
    }
  };

  const isUploadDisabled: boolean = !file || isSaving || uploadState.status === 'uploading';

  return (
    <AppModal show={show} onHide={onClose} backdrop="static" keyboard={!isSaving}>
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title>{
          i18next.t(isCompletion
            ? 'patientFileUploadModal.uploadCompletionFile'
            : 'patientFileUploadModal.uploadApplicationFile')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="position-relative">
          <DropZone
            label={i18next.t('patientFileUploadModal.dropzoneLabel')}
            accept="application/pdf"
            previewUrl={previewUrl ?? ''}
            disabled={isSaving || uploadState.status === 'uploading'}
            onError={(message: string) => console.error(message)}
            onFileSelect={handleFileSelect}
            onClear={handleClear}
          />

          <UploadOverlay progress={uploadState.progress} status={uploadState.status} />
        </div>

      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          type="button"
          onClick={handleHide}
          disabled={isSaving}
        >
          {i18next.t('generic.cancel')}
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={handleSave}
          disabled={isUploadDisabled}
        >
          {i18next.t('generic.save')}
        </Button>
      </Modal.Footer>
    </AppModal>
  );
};


export default PatientFileUploadModal;
